/**
 * Parser for `<internal>...</internal>` blocks in agent messages.
 *
 * The agent's persona instructs it to wrap process narration (tool
 * names, REST endpoints, internal phase names, file paths it just
 * edited) inside `<internal>...</internal>` tags. The chat UI
 * collapses those blocks into a "Process details" disclosure so the
 * user sees only the product-language content by default.
 *
 * This module operates on the *raw* markdown text BEFORE any markdown
 * parsing happens — so the placeholder logic can skip fenced code
 * blocks (where the agent might legitimately quote `<internal>` as
 * literal text in an explanation about itself).
 *
 * Ported from PitchPilot main (PR #105) verbatim; PP main is the
 * source of truth for this parser and CF mirrors it so output looks
 * the same on both products.
 *
 * Design notes:
 *   - We do NOT use a regex that pretends to handle `<internal>`
 *     surrounded by ``` fences. The cleanest way is a lightweight
 *     tokenizer that knows about ``` and ~~~ fences and only looks
 *     at `<internal>` outside of them.
 *   - The matching is non-nested: `<internal>` opens a block, the
 *     next `</internal>` (outside of code fences) closes it. Nested
 *     opens are absorbed as literal text inside the block — agents
 *     shouldn't nest, but if they do we don't blow up.
 *   - Unbalanced (open tag with no close) is handled by treating
 *     everything after the open as a single internal block. This
 *     matches the user-facing intent: better to collapse the rest
 *     than to dump a stray `<internal>` into the chat.
 */

export type InternalSegment =
  | { kind: 'md'; text: string }
  | { kind: 'internal'; text: string };

const OPEN = '<internal>';
const CLOSE = '</internal>';

/**
 * Split raw markdown into a list of segments. Markdown segments are
 * meant to be rendered through `marked`; internal segments are meant
 * to be rendered through the collapsible disclosure component.
 *
 * Order is preserved: a message that goes
 *   "Hi <internal>using-X</internal> here's the deck <internal>save-Y</internal>"
 * becomes [md, internal, md, internal].
 */
export function parseInternalBlocks(raw: string): InternalSegment[] {
  if (!raw) return [{ kind: 'md', text: '' }];

  const segments: InternalSegment[] = [];
  // We walk line-by-line because fence state is a per-line concept,
  // but inside non-fence lines we want character-level resolution so
  // multiple `<internal>` blocks on the same line still work.
  const lines = raw.split('\n');

  let inFence = false;
  let fenceMarker = '';
  let inInternal = false;

  let mdBuf = '';
  let intBuf = '';

  const flushMd = () => {
    if (mdBuf.length > 0) {
      segments.push({ kind: 'md', text: mdBuf });
      mdBuf = '';
    }
  };
  const flushInternal = () => {
    if (intBuf.length > 0) {
      segments.push({ kind: 'internal', text: intBuf.replace(/^\n+|\n+$/g, '') });
      intBuf = '';
    }
  };

  const appendChunk = (chunk: string) => {
    if (inInternal) intBuf += chunk;
    else mdBuf += chunk;
  };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const isLast = li === lines.length - 1;
    const trimmed = line.trimStart();

    if (!inFence) {
      if (trimmed.startsWith('```')) {
        inFence = true;
        fenceMarker = '```';
        appendChunk(line + (isLast ? '' : '\n'));
        continue;
      }
      if (trimmed.startsWith('~~~')) {
        inFence = true;
        fenceMarker = '~~~';
        appendChunk(line + (isLast ? '' : '\n'));
        continue;
      }
    } else {
      appendChunk(line + (isLast ? '' : '\n'));
      if (trimmed.startsWith(fenceMarker)) {
        inFence = false;
        fenceMarker = '';
      }
      continue;
    }

    let cursor = 0;
    while (cursor < line.length) {
      if (inInternal) {
        const closeIdx = line.indexOf(CLOSE, cursor);
        if (closeIdx === -1) {
          intBuf += line.slice(cursor);
          cursor = line.length;
        } else {
          intBuf += line.slice(cursor, closeIdx);
          flushInternal();
          inInternal = false;
          cursor = closeIdx + CLOSE.length;
        }
      } else {
        const openIdx = line.indexOf(OPEN, cursor);
        if (openIdx === -1) {
          mdBuf += line.slice(cursor);
          cursor = line.length;
        } else {
          mdBuf += line.slice(cursor, openIdx);
          flushMd();
          inInternal = true;
          cursor = openIdx + OPEN.length;
        }
      }
    }

    if (!isLast) {
      if (inInternal) intBuf += '\n';
      else mdBuf += '\n';
    }
  }

  flushInternal();
  flushMd();

  if (segments.length === 0) return [{ kind: 'md', text: '' }];
  return segments;
}

/**
 * True if the raw text contains at least one fully-closed
 * `<internal>...</internal>` block (outside of code fences), OR an
 * unclosed open tag. Used by the renderer to decide whether to take
 * the segment-rendering path at all.
 */
export function hasInternalBlock(raw: string): boolean {
  if (!raw) return false;
  if (raw.indexOf(OPEN) === -1) return false;
  const segs = parseInternalBlocks(raw);
  return segs.some((s) => s.kind === 'internal');
}

/**
 * True if the raw text has an open `<internal>` tag that has not yet
 * been closed (outside of code fences). Streaming UIs use this to
 * decide whether to suppress the trailing partial content until the
 * stream completes.
 */
export function hasUnclosedInternal(raw: string): boolean {
  if (!raw) return false;
  if (raw.indexOf(OPEN) === -1) return false;

  const lines = raw.split('\n');
  let inFence = false;
  let fenceMarker = '';
  let inInternal = false;

  for (const line of lines) {
    const trimmed = line.trimStart();
    if (!inFence) {
      if (trimmed.startsWith('```')) { inFence = true; fenceMarker = '```'; continue; }
      if (trimmed.startsWith('~~~')) { inFence = true; fenceMarker = '~~~'; continue; }
    } else {
      if (trimmed.startsWith(fenceMarker)) { inFence = false; fenceMarker = ''; }
      continue;
    }
    let cursor = 0;
    while (cursor < line.length) {
      if (inInternal) {
        const closeIdx = line.indexOf(CLOSE, cursor);
        if (closeIdx === -1) { cursor = line.length; }
        else { inInternal = false; cursor = closeIdx + CLOSE.length; }
      } else {
        const openIdx = line.indexOf(OPEN, cursor);
        if (openIdx === -1) { cursor = line.length; }
        else { inInternal = true; cursor = openIdx + OPEN.length; }
      }
    }
  }
  return inInternal;
}
